// ════════════════════════════════════════════════════════════
//  climb/climb.js — THE CLIMB (pure: no React, no Firebase, no clock)
// ════════════════════════════════════════════════════════════
//  caP'n 2026-10-17: "Shatterproof needs more 'end game' grind like the League
//  does but for real players, that way Lyraei and other partners have more stuff
//  to grind for" · "the Elo idea where the player has to fight through all the
//  contenders and then they can fight the circuit."
//
//  A totem, borrowed from Terror's V'yen's Gauntlet (read-only reference):
//   · RUNGS of bouts. The whole stack of a rung is rolled when you start it and
//     saved with the run, so you can see it and plan, and backing out can't
//     re-roll a soft opponent.
//   · ONLY ANIMA CARRIES between bouts (caP'n: yes), from the beings still
//     standing; a being ruined last bout walks in ruined (the fight's one
//     resurrection can bring it back). Everything else starts fresh.
//   · Every NEW RUNG starts everyone whole again (Terror's rule: "every rung it
//     refreshes") — so a brutal rung is hard, never a run poisoned forever.
//   · The Contenders first, weakest to strongest; then the Circuit; THE LEAGUE'S
//     #1 (the table's leader) is the last bout of all — the final boss.
//   · Your standing is a rating on THE LEAGUE'S OWN SCALE (caP'n: "The League's
//     elo"): same start, same K, same formula, so "you'd sit #9 among the 32"
//     means what it says.
//   · Each win pays the game's usual Mini Dust; clearing a rung and the summit pay
//     KING'S KRESTS (caP'n 2026-10-20: "Yes, call it King's Krests").
//   · YOUR THREE ARE LOCKED for the run (picked when it starts), so a ruined
//     being can't be swapped for a fresh one between bouts.
//   · Walking out of a bout, or giving up between bouts, counts as losing the
//     next bout — no re-rolling a bad draw, no banking rating before a boss.
//   · A rung you already cleared can be REPLAYED as practice: it pays the
//     usual win purse but moves no rating and pays no rung purse (so the
//     easiest rung can't be farmed). Claude's call 2026-10-18, a dial below.
//  SCAFFOLD (2026-10-18): placeholder rung names, browser-saved progress
//  (climbStore.js). The rules are here so the screen, the check and Lyraei's
//  seat all get the same answer.
// ════════════════════════════════════════════════════════════
import { ELO_START, ELO_K, eloP } from "../online/circuit.js";

// ── DIALS ────────────────────────────────────────────────────
// The ladder. Each Contenders rung takes the next weakest Contenders; each Circuit rung the next
// weakest of the Circuit; the very last bout is the League's #1. Sizes may not add up to the
// division: a short division just makes the last rung shorter, a long one leaves the extra out.
export const CLIMB_RUNGS = [                                     // ⟵ DIAL — names are placeholders
  { id: "c1", division: "contenders", bouts: 1, title: "The Taster" },
  { id: "c2", division: "contenders", bouts: 3, title: "Contenders I" },
  { id: "c3", division: "contenders", bouts: 4, title: "Contenders II" },
  { id: "c4", division: "contenders", bouts: 4, title: "Contenders III" },
  { id: "c5", division: "contenders", bouts: 4, title: "The Contenders' Gate" },
  { id: "k1", division: "circuit", bouts: 4, title: "Circuit I" },
  { id: "k2", division: "circuit", bouts: 4, title: "Circuit II" },
  { id: "k3", division: "circuit", bouts: 4, title: "Circuit III" },
  { id: "k4", division: "circuit", bouts: 4, title: "The Summit" },
];
export const CLIMB_RUNG_PURSE = 1;     // ⟵ DIAL — King's Krests for clearing a rung (a bout win pays the game's usual Mini Dust)
export const CLIMB_SUMMIT_PURSE = 1;   // ⟵ DIAL — King's Krests, on top, for beating the League's #1
export const REPLAY_MOVES_RATING = false;   // ⟵ DIAL — does a replayed (already cleared) rung move your rating?
export const CLIMB_START_ELO = ELO_START;   // you start where every League rookie starts
export const CLIMB_K = ELO_K;               // and move like they do

// ── who is where (from the League's live snapshot, or its genesis) ──
/**
 * Every rung's opponents, in order. league = { circuit:[keys], contenders:[keys] } (this week's divisions);
 * eloOf(key) → rating; number1 = the League's #1 (the table's leader; defaults to the strongest of the Circuit).
 * Returns [{ ...rung, index, keys:[…] }].
 */
export function ladderOf({ league, eloOf, number1 = null }) {
  const asc = (keys) => [...keys].sort((a, b) => eloOf(a) - eloOf(b) || (a < b ? -1 : a > b ? 1 : 0));
  const circ = [...new Set(league.circuit || [])];
  const pools = { contenders: asc([...new Set(league.contenders || [])].filter((k) => !circ.includes(k))), circuit: asc(circ) };
  const top = number1 && pools.circuit.includes(number1) ? number1 : pools.circuit[pools.circuit.length - 1] || null;
  if (top) pools.circuit = [...pools.circuit.filter((k) => k !== top), top];   // the #1 is always the last bout of all
  const used = { contenders: 0, circuit: 0 };
  const lastOf = {}; CLIMB_RUNGS.forEach((r, i) => { lastOf[r.division] = i; });
  return CLIMB_RUNGS.map((r, index) => {
    const pool = pools[r.division] || [], from = used[r.division];
    // the last rung of a division always ends on that division's strongest (the #1 for the Circuit)
    let keys = pool.slice(from, from + r.bouts);
    if (index === lastOf[r.division] && pool.length > from + r.bouts) keys = [...pool.slice(from, from + r.bouts - 1), pool[pool.length - 1]];
    used[r.division] = from + keys.length;
    return { ...r, index, keys };
  }).filter((r) => r.keys.length);
}

// ── your record (what's saved between visits) ──
//  top = the id of the highest rung you've cleared (an id, not a place, so reordering the dial
//  never points your progress at the wrong rung; an id that no longer exists starts you over).
export const blankRecord = () => ({ v: 2, elo: CLIMB_START_ELO, top: null, runs: 0, won: 0, lost: 0, summits: 0 });
export function normalRecord(rec) {
  const r = { ...blankRecord(), ...(rec || {}) };
  if (r.top == null && Number.isInteger(r.cleared) && r.cleared >= 0 && CLIMB_RUNGS[r.cleared]) r.top = CLIMB_RUNGS[r.cleared].id;   // v1 saves
  delete r.cleared; delete r.best; r.v = 2;
  return r;
}
export const topIndex = (rec) => (rec && rec.top ? CLIMB_RUNGS.findIndex((x) => x.id === rec.top) : -1);
export const isCleared = (rec, rungIndex) => rungIndex <= topIndex(rec);
export const openRungs = (rec, ladder) => ladder.filter((r) => r.index <= topIndex(rec) + 1).map((r) => r.index);
/** Where your rating would sit among the League's 32 (1-based), given everyone's ratings. */
export const wouldSit = (elo, allElos) => 1 + allElos.filter((x) => x > elo).length;

// ── a run ──
/** Roll a rung's totem: each bout is { key, name, team, style, elo } fixed now, saved with the run — and so are YOUR three. */
export function startRun({ rung, entrantOf, eloOf, number1 = null, at = 0, team = null, tomes = null, rec = null }) {
  const totem = rung.keys.map((key, i) => {
    const e = entrantOf(key) || { key, name: key, team: [], style: null };
    return { key, name: e.name, team: e.team.slice(), style: e.style || null, elo: Math.round(eloOf(key) * 10) / 10, boss: i === rung.keys.length - 1, top: key === number1 };
  });
  return { v: 2, rung: rung.index, rungId: rung.id, title: rung.title, bout: 0, carry: null, totem, live: false, startedAt: at,
    team: team ? team.slice() : null, tomes: tomes ? tomes.slice() : null, replay: !!rec && isCleared(rec, rung.index) };
}
export const boutOf = (run) => (run && run.totem ? run.totem[run.bout] || null : null);

/** What your three walk out of a fight with: Anima per being in roster order (a ruined one carries 0). */
export const carryOut = (battle, side = 0) => battle.sides[side].roster.map((r) => (r.ruined ? 0 : Math.max(0, Math.round(r.anima))));

/** The League's rating move for one bout (the same formula the League uses). */
export const eloAfter = (mine, theirs, won) => mine + CLIMB_K * ((won ? 1 : 0) - eloP(mine, theirs));

/**
 * One bout is over. Pure: returns { run, rec, outcome, pay } —
 *   outcome "next" (another bout, on what you carried) · "rung_cleared" · "summit" (beat the #1) · "run_over".
 *   pay = King's Krests owed (rung / summit) ON TOP of the bout's own Mini Dust win purse, for the caller to grant.
 */
export function finishBout(run, rec, { won, carry }) {
  const opp = boutOf(run);
  const r = normalRecord(rec);
  if (opp) {
    if (!run.replay || REPLAY_MOVES_RATING) r.elo = Math.round(eloAfter(r.elo, opp.elo, won) * 10) / 10;
    r[won ? "won" : "lost"]++;
  }
  if (!won || !opp) return { run: null, rec: r, outcome: "run_over", pay: 0 };
  const next = run.bout + 1;
  if (next < run.totem.length) return { run: { ...run, bout: next, carry: carry.slice(), live: false }, rec: r, outcome: "next", pay: 0 };
  const idx = CLIMB_RUNGS.findIndex((x) => x.id === run.rungId);
  if (idx > topIndex(r)) r.top = run.rungId;
  let pay = run.replay ? 0 : CLIMB_RUNG_PURSE, outcome = "rung_cleared";
  if (opp.top) { r.summits = (r.summits || 0) + 1; if (!run.replay) pay += CLIMB_SUMMIT_PURSE; outcome = "summit"; }
  return { run: null, rec: r, outcome, pay };
}
/** A run abandoned (left mid-bout, or given up between bouts): it counts as the current bout lost. */
export const abandon = (run, rec) => finishBout(run, rec, { won: false, carry: [] });
